import React, { useEffect, useState } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { INPUT_STYLES } from '../../Constants/Const';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { toast } from 'sonner';
import { getImagePath } from '../../Utils/Utilities';
import { useBrand } from '../../Utils/BrandContext';
import { getEditionPricing, getLowestPricedEdition, getOfferPrice, getPageActiveOffer } from '../../Constants/Packages';

const HeroSectionCompact = ({ onGetStarted, onLogin, loading, logo = getImagePath("/Images/QikERP.png"), keyPoints = [], isFromIdx = false, onPricing, onVisit, pageName = "GPOWER" }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [ready, setReady] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (ready) {
      onVisit?.();
    }
  }, [ready]);

  const { brand: activeBrand } = useBrand();

  if (!ready) return (
    <section id="hero-section" className="min-h-screen bg-background-light" />
  );

  // Get pricing for QikERP
  const activeEdition = getLowestPricedEdition(pageName);
  const activeOffer = getPageActiveOffer(pageName);
  const offerPrice = getEditionPricing(activeEdition, activeOffer);
  const hasAnyOffer = !!activeOffer && activeOffer?.offerKey !== 'FRESH';
  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  const handleSubmit = (e, action) => {
    e.preventDefault();

    if (!mobileNumber) {
      toast.error('Please enter your mobile number');
      return;
    }

    if (mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    action === 'login' ? onLogin?.(mobileNumber) : onGetStarted?.(mobileNumber);
  };

  const defaultKeyPoints = [
    'Simpler & Smarter than Excel',
    'Multi-User Packages',
    'Mobile & Desktop Access'
  ];

  const displayKeyPoints = keyPoints.length > 0 ? keyPoints : defaultKeyPoints;

  if (isMobile) {
    return (
      <section id="hero-section" className="min-h-[30vh] bg-background-light md:min-h-screen flex items-center justify-center pt-6 px-4 md:py-0 overflow-hidden">
        <Container>
          <div className="text-center">
            <img src={activeBrand == 'bz' ? getImagePath('/Images/Biziverse.png') : getImagePath("/Images/QikERPBy.png")} alt="QikERP" className={`mx-auto m-4 ${activeBrand == 'bz' ? 'h-[120px]' : 'h-[136px]'} `} />
            {/* <h1 className="text-[24px] sm:text-4xl font-bold text-center mb-8">
              <span className="text-primary">Easy</span> & <span className="text-primary">Powerful</span>
              <br />
              <span>Tools to</span>
              <br />
              <span className="text-primary">Accelerate Business</span>
            </h1> */}

            {/* Images Section */}
            <div className="relative w-full max-w-[500px] mx-auto">
              <img
                src={getImagePath("/Images/Features/HeroImage.webp")}
                alt="Dashboard"
                className="w-full h-auto block"
                loading="eager"
              />
            </div>

            {/* Key Points Only */}
            {/* <div className="bg-background-light border-2 border-border rounded p-6 max-w-md mx-auto">
              <h2 className="text-base font-semibold text-[#333] mb-4 text-center">
                Why Choose Us
              </h2>
              <div className="space-y-2">
                {displayKeyPoints.map((point, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-[#333]">
                    <span className="text-primary font-bold">✓</span>
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </Container>
      </section>
    );
  }

  // Desktop Layout
  return (
    <section id="hero-section" className="min-h-screen bg-[#f8f9fa] flex items-center justify-center py-0 overflow-visible">
      <div className="w-full max-w-[100%] mx-[2%] px-8">
        {/* Title at Top - Single Line */}
        <div className="text-center mb-16 mt-10">
          <h1 className="text-5xl lg:text-5xl font-bold">
            <span className="text-primary">Easy</span> & <span className="text-primary">Powerful</span> Tools to <span className="text-primary">Accelerate Business</span>
          </h1>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-3 items-center">
          <div className="relative flex items-center justify-center col-span-2">
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '7px',
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img
                src={getImagePath("/Images/Features/HeroImage.webp")}
                alt="Dashboard"
                style={{
                  height: 'auto',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  borderRadius: '10px',
                  display: 'block'
                }}
              />
            </div>
          </div>

          {/* Right - Get Started Card */}
          <div className="flex justify-center">
            <div className="bg-white border-2 border-border rounded-[10px] px-10 py-4 shadow-lg w-full max-w-sm">
              <img src={activeBrand == 'bz' ? getImagePath('/Images/Biziverse.png') : logo} alt="Logo" className={`mx-auto m-8 mt-4 ${activeBrand == 'bz' ? 'h-16' : 'h-24'}`} />

              {/* Tagline */}
              <div className="text-center mb-4">
                <h3 className="text-[23px] font-bold text-[#333] m-0 leading-[1.7]">
                  <span className="text-[#333]">Powerful, Multi-User</span>
                  <br />
                  <span className="text-[#333]"><span className='text-primary'>ERP</span> + <span className='text-primary'>CRM</span> Software</span>
                  <br />
                  <span className="text-gray-500">@</span>
                  <br />
                  <span className="text-[#333]">Unbeatable Price</span>
                </h3>

                {/* <div className="my-1">
                  <p className="text-[18px] text-text-secondary font-medium mb-1">Everything @
                    <span className="text-[15px] text-text-secondary">₹ <span className='line-through text-[20px]'>{offerPrice.strikePrice} / yr</span></span>
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-[20px] text-text-secondary">₹</span>
                    <span className="text-[36px] font-bold text-primary">{offerPrice.mainPrice}</span>
                  </div> */}
                  {/* <span className="text-[16px] text-text-secondary">{(activeEdition.id === 37 || activeEdition.id === 38) ? 'for 1st year' : 'per year'} +GST</span> */}
                  {/* <span className="text-[16px] text-text-secondary">{offerPrice.isOffer ? 'for 1st year +GST' : 'per year +GST'}</span> */}
                  {/* <span className="text-[16px] text-text-secondary">{ offerPrice.isBogo ? 'for 2 years +GST' : (offerPrice.isOffer ? 'for 1st year +GST' : 'per year +GST')}</span> */}
                  {/* <span className="text-[16px] text-text-secondary">per year +GST</span>
                </div> */}

                <div className="my-1">
                  <p className="text-[18px] text-text-secondary font-medium mb-1">Everything @
                    {activeEdition?.stdAmt > activeEdition?.baseAmt && (
                      <span className="text-[15px] text-text-secondary"> ₹ <span className='line-through text-[20px]'>{activeEdition.stdAmtStr} / yr</span></span>
                    )}
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-[20px] text-text-secondary">₹</span>
                    <span className="text-[36px] font-bold text-primary">{activeEdition?.baseAmtStr}</span>
                  </div>
                  <span className="text-[16px] text-text-secondary">per year +GST</span>
                </div>

                {!isFromIdx &&
                  <Button
                    type="submit"
                    className="flex-1 mt-4"
                    onClick={onPricing}
                  >
                    Buy Now
                  </Button>}

                {!isFromIdx && hasAnyOffer && (
                  <>
                    <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                    <p
                      className="m-0 mt-4 text-sm font-semibold text-center text-red-500"
                      style={{ animation: 'blink 1s step-start infinite' }}
                    >
                      {offerPrice?.isBogo ? 'Check BOGO Offer!' : 'Check Discount Offer!'}
                    </p>
                  </>
                )}
              </div>

              {/* Key Points */}
              {/* <div className="space-y-3">
                {displayKeyPoints.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="text-primary font-bold text-lg leading-none mt-0.5">✓</span>
                    <span className="text-sm text-[#333]">{point}</span>
                  </div>
                ))}
              </div> */}

              {/* {isFromIdx && <div className='flex items-center justify-center'>
                <div className='w-[75%] h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent my-4'></div>
              </div>} */}

              {/* <h2 className="text-lg font-semibold text-[#333] mb-4 text-center">
                Enter Mobile to Get Started
              </h2> */}

              {isFromIdx && (<form onSubmit={(e) => handleSubmit(e, 'start')} className="mb-6">
                <div className={`${INPUT_STYLES.wrapper} w-full mb-4`}>
                  <input
                    type="tel"
                    id="mobile"
                    value={mobileNumber}
                    onChange={handleInputChange}
                    placeholder="Enter Mobile to Get Started"
                    maxLength="10"
                    className="w-full px-4 py-3 border-none outline-none focus:outline-none focus:ring-0 focus:border-transparent text-base bg-background-light"
                    required
                    autoFocus
                    disabled={loading}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1"
                    onClick={(e) => handleSubmit(e, 'login')}
                    disabled={loading}
                  >
                    Login
                  </Button>
                  <Button
                    type="submit"
                    variant="secondary"
                    className="flex-1 mobile-btn bg-primary-subtle hover:bg-primary-subtle"
                    disabled={loading}
                  >
                    Start Now
                  </Button>
                </div>
              </form>)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionCompact;
